import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtFecha } from "../../lib/format";
import { StatusBadge } from "../StatusBadge";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

const NOMBRES = {
  capa1: "1 · Lectura del documento", capa2: "2 · Cruce y validaciones", capa3: "3 · Comparación facial",
  capa4: "4 · Prueba de vida", capa5: "5 · Duplicados y señales", capa6: "6 · Puntaje y resultado",
};
const RES = { ok: "ok", con_observaciones: "pendiente_verificacion", fallido: "fallido", identidad_verificada: "ok", en_revision: "pendiente_verificacion", rechazo_sugerido: "fallido" };
const EST = { coincide: "text-emerald-700", difiere: "text-rose-700 font-semibold", no_disponible: "text-slate-400" };

const Campos = ({ campos }) => (
  <table className="w-full text-xs mt-2">
    <thead><tr className="text-left text-slate-500"><th>Campo</th><th>Dato usado</th><th>MRZ</th><th>Código</th><th>Frente</th><th>Estado</th></tr></thead>
    <tbody>
      {Object.entries(campos).map(([k, c]) => (
        <tr key={k} className="border-t">
          <td className="py-1">{k}</td><td>{c.tipeado || "—"}</td>
          {["mrz", "codigo", "ocr_frente"].map((s) => <td key={s} className={EST[c.fuentes[s]]}>{c.fuentes[s]}</td>)}
          <td className={EST[c.estado]}>{c.estado}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Layer = ({ id, c }) => {
  const [open, setOpen] = useState(false);
  const det = { ...c.detalle };
  delete det.campos;
  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid={`layer-${id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-sm">{NOMBRES[id]}</b>
        <StatusBadge value={RES[c.resultado]} label={c.resultado} />
        <span className="font-mono text-xs">puntaje {c.puntaje}</span>
        {c.motivos.map((m) => <code key={m} className="text-[11px] bg-rose-50 text-rose-700 px-1.5 rounded">{m}</code>)}
      </div>
      <p className="text-[11px] text-slate-500 mt-1">Versión: {Object.values(c.version || {}).join(" · ")}</p>
      {c.detalle?.campos && <Campos campos={c.detalle.campos} />}
      <button className="text-xs underline mt-1" onClick={() => setOpen(!open)} data-testid={`layer-${id}-toggle`}>{open ? "Ocultar detalle" : "Ver detalle"}</button>
      {open && <pre className="text-[10px] bg-slate-50 p-2 rounded max-h-60 overflow-auto whitespace-pre-wrap">{JSON.stringify(det, null, 1)}</pre>}
    </div>
  );
};

export const LayerResults = ({ s }) => {
  if (s.estado_proceso !== "completado") {
    return <p className="text-sm text-amber-700" data-testid="kyc-processing-status">Motor KYC: {s.estado_proceso || "sin procesar"}{s.error_proceso ? ` (${s.error_proceso})` : ""}</p>;
  }
  const r = s.resultado_capas;
  return (
    <div className="space-y-2" data-testid="kyc-layers">
      <div className="flex items-center gap-3">
        <span className="font-heading text-2xl font-bold" data-testid="kyc-score">{s.puntaje}</span>
        <StatusBadge value={RES[s.resultado_kyc]} label={s.resultado_kyc} testId="kyc-result-badge" />
        <span className="text-xs text-slate-500">"identidad_verificada" no es cliente aprobado</span>
        {s.duracion_proceso_ms != null && <span className="text-xs text-slate-600" data-testid="kyc-processing-time">Tiempo total de procesamiento: {(s.duracion_proceso_ms / 1000).toFixed(1).replace(".", ",")} s</span>}
      </div>
      {["capa1", "capa2", "capa3", "capa4", "capa5", "capa6"].map((k) => <Layer key={k} id={k} c={r[k]} />)}
      <p className="text-[11px] text-slate-500">Proveedor externo: {r.proveedor_externo?.verify_document?.proveedor} ({r.proveedor_externo?.verify_document?.resultado})</p>
    </div>
  );
};

export const ReviewActions = ({ s, rol, onDone }) => {
  const [motivo, setMotivo] = useState("");
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const needs = ["en_revision", "rechazo_sugerido"].includes(s.resultado_kyc);
  return (
    <div className="space-y-3 border-t pt-3">
      <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/company/kyc/sesiones/${s.id}/reprocesar`), "Reprocesando…")} data-testid="kyc-reprocess-button">Reprocesar</Button>
      {s.revision && <p className="text-sm" data-testid="kyc-revision-info"><b>Revisión del oficial:</b> {s.revision.decision} · {s.revision.oficial} · {fmtFecha(s.revision.fecha)} · {s.revision.motivo}</p>}
      {needs && rol === "compliance_officer" && !s.revision && (
        <div className="space-y-2">
          <Textarea placeholder="Motivo de la decisión (obligatorio, mínimo 10 caracteres)" value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="kyc-review-motivo" />
          <div className="flex gap-2">
            {[["confirma_identidad", "Confirmar identidad"], ["confirma_rechazo", "Confirmar rechazo"]].map(([d, l]) => (
              <Button key={d} size="sm" disabled={motivo.trim().length < 10} className="bg-slate-900 hover:bg-slate-800"
                onClick={() => act(() => api.post(`/company/kyc/sesiones/${s.id}/revision`, { decision: d, motivo }), "Revisión registrada")} data-testid={`kyc-review-${d}`}>{l}</Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const CompletePresencial = ({ sid, operador, onDone }) => {
  const [ok, setOk] = useState(false);
  const send = async () => {
    try {
      await api.post(`/company/kyc/sesiones/${sid}/completar-presencial`, { confirmo: ok });
      toast.success("Verificación presencial registrada");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3" data-testid="complete-presencial">
      <label className="flex gap-3 items-start text-sm">
        <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1 h-5 w-5" data-testid="complete-presencial-checkbox" />
        <span>Verifiqué presencialmente que la cara de la persona coincide con el documento.<span className="block text-xs text-slate-500">{operador} · {fmtFecha(new Date().toISOString())}</span></span>
      </label>
      <Button size="sm" disabled={!ok} onClick={send} className="bg-slate-900 hover:bg-slate-800" data-testid="complete-presencial-submit">Registrar verificación presencial</Button>
    </div>
  );
};
